import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../store';
import { ARCHIVE_TEAMS } from '../data';
import Panel from '../ui/Panel';
import Btn from '../ui/Btn';
import Chip from '../ui/Chip';
import Spinner from '../ui/Spinner';
import PremierBadge from '../cs/PremierBadge';

const ROLE_LABEL = { igl: 'IGL', awp: 'AWPer', entry: 'Entry', support: 'Support', lurk: 'Lurker' };
const ROLE_COLOR = {
  igl: 'var(--accent)', awp: 'var(--gold)',
  entry: '#58A6FF', support: '#4ADE80', lurk: '#C084FC',
};

/* Призовые места турнира — подсветка карточек победителей (имя нормализуется в UPPERCASE) */
const TEAM_PLACEMENTS = {
  'QUMYS ESPORTS': 1,
  'CYBERLAB': 2,
  'TEAM SOUNDCLOUD': 3,
};
const placementOf = (name) => TEAM_PLACEMENTS[(name || '').trim().toUpperCase()] || 0;
const PLACEMENT_META = {
  1: { color: '#E6B73C', deep: '#B98B17', glow: 'rgba(230,183,60,0.45)' },
  2: { color: '#C7CED8', deep: '#7E8794', glow: 'rgba(199,206,216,0.32)' },
  3: { color: '#CD7F32', deep: '#8E531F', glow: 'rgba(205,127,50,0.40)' },
};

function TrophyIco({ size = 12, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0zM7 4H4v2a3 3 0 0 0 3 3M17 4h3v2a3 3 0 0 1-3 3" />
    </svg>
  );
}

function PlacementBadge({ rank, t }) {
  const meta = PLACEMENT_META[rank];
  const label = rank === 1 ? t('teams.champion') : rank === 2 ? t('teams.place2') : t('teams.place3');
  return (
    <span style={{
      display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
      fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
      padding: '3px 9px', borderRadius: 20, textTransform: 'uppercase',
      background: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}66`,
      boxShadow: rank === 1 ? `0 0 14px ${meta.glow}` : 'none',
    }}>
      <TrophyIco color={meta.color} />
      {label}
    </span>
  );
}

function Avatar({ name, size = 38, fontSize = 14 }) {
  const initials = (name || '??').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div className="center" style={{
      width: size, height: size, borderRadius: 'var(--r-sm)', flexShrink: 0,
      background: 'linear-gradient(135deg, var(--accent-deep), var(--surface-3))',
      border: '1px solid var(--line-accent)',
      fontFamily: 'var(--font-display)', fontWeight: 700, fontSize,
    }}>{initials}</div>
  );
}

function RoleBadge({ role }) {
  return (
    <span style={{
      fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
      padding: '2px 7px', borderRadius: 4, textTransform: 'uppercase',
      background: `${ROLE_COLOR[role] || 'var(--fg-muted)'}18`,
      color: ROLE_COLOR[role] || 'var(--fg-muted)',
      border: `1px solid ${ROLE_COLOR[role] || 'var(--line)'}40`,
    }}>{ROLE_LABEL[role] || role}</span>
  );
}

function StatusBadge({ status, t }) {
  const map = {
    recruiting: { label: t('teams.filterRecruiting'), color: 'var(--ok)' },
    moderation: { label: t('teams.onModeration'),      color: 'var(--info)' },
    full:       { label: t('teams.teamFull'),          color: 'var(--fg-muted)' },
    closed:     { label: t('teams.closedRecruiting'),  color: 'var(--danger)' },
  };
  const s = map[status] || { label: status, color: 'var(--fg-muted)' };
  return (
    <span style={{
      fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
      padding: '3px 9px', borderRadius: 20, textTransform: 'uppercase',
      background: `${s.color}18`, color: s.color, border: `1px solid ${s.color}40`,
      display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
      {s.label}
    </span>
  );
}

/* ── Responsive modal/sheet ───────────────────────────────── */
function useIsDesktop() {
  const [desktop, setDesktop] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 768);
  useEffect(() => {
    const h = () => setDesktop(window.innerWidth >= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return desktop;
}

function BottomSheet({ open, onClose, children, zIndex = 200 }) {
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  const content = (
    <>
      <style>{`
        @keyframes sheetUp   { from { transform: translateY(100%) } to { transform: translateY(0) } }
        @keyframes modalIn   { from { opacity: 0; transform: translateY(-12px) scale(0.97) } to { opacity: 1; transform: translateY(0) scale(1) } }
        @keyframes fadeIn    { from { opacity: 0 } to { opacity: 1 } }
      `}</style>

      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex,
          background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
          animation: 'fadeIn .2s ease both',
        }}
      />

      {isDesktop ? (
        /* ── Desktop: centered modal ── */
        <div style={{
          position: 'fixed', inset: 0, zIndex: zIndex + 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '24px',
          pointerEvents: 'none',
        }}>
          <div style={{
            width: '100%', maxWidth: 560,
            maxHeight: '85vh',
            background: 'var(--surface-2)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-xl)',
            display: 'flex', flexDirection: 'column',
            animation: 'modalIn .22s cubic-bezier(0.32,0.72,0,1) both',
            boxShadow: '0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)',
            pointerEvents: 'all',
          }}>
            {/* Close button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '14px 16px 0', flexShrink: 0 }}>
              <button onClick={onClose} style={{
                width: 32, height: 32, borderRadius: 8, border: '1px solid var(--line)',
                background: 'var(--surface-3)', color: 'var(--fg-muted)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'color .15s, border-color .15s',
              }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--fg)'; e.currentTarget.style.borderColor = 'var(--line-strong)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--fg-muted)'; e.currentTarget.style.borderColor = 'var(--line)'; }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {children}
            </div>
          </div>
        </div>
      ) : (
        /* ── Mobile: bottom sheet ── */
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: zIndex + 1,
          background: 'var(--surface-2)',
          borderTop: '1px solid var(--line)',
          borderRadius: '20px 20px 0 0',
          maxHeight: '90dvh',
          display: 'flex', flexDirection: 'column',
          animation: 'sheetUp .3s cubic-bezier(0.32,0.72,0,1) both',
          boxShadow: '0 -8px 40px rgba(0,0,0,0.5)',
        }}>
          <div style={{ padding: '12px 0 0', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
            <div style={{ width: 40, height: 4, borderRadius: 2, background: 'var(--line-strong)' }} />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {children}
          </div>
        </div>
      )}
    </>
  );

  return createPortal(content, document.body);
}

/* ── Player detail sheet ──────────────────────────────────── */
function PlayerSheet({ member, team, open, onClose, onBack, onJoin, joining, user }) {
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  if (!member) return null;

  const u = member.user || {};
  const isCaptain = team?.captain_id === member.user_id;
  const isMember = user && team?.members?.some(m => m.user_id === user.id);
  const isSelf = user && user.id === member.user_id;
  const slots = 5 - (team?.players_count || team?.members?.length || 0);
  const canJoin = !isMember && !isSelf && team?.status === 'recruiting' && slots > 0;

  return (
    <BottomSheet open={open} onClose={onClose} zIndex={300}>
      <div style={{ padding: '16px 20px 32px' }}>
        {/* Back — mobile only */}
        {!isDesktop && (
          <button onClick={onBack} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--accent-bright)', fontFamily: 'var(--font-mono)', fontSize: 12,
            letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 6,
            padding: '4px 0', marginBottom: 20,
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5m7-7-7 7 7 7" /></svg>
            {team?.name}
          </button>
        )}

        {/* Player header */}
        <div className="col" style={{ alignItems: 'center', gap: 12, marginBottom: 28 }}>
          <div className="center" style={{
            width: 80, height: 80, borderRadius: 'var(--r-md)',
            background: 'linear-gradient(135deg, var(--accent-deep), var(--surface-3))',
            border: '2px solid var(--line-accent)',
            fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 28,
            boxShadow: '0 0 24px -8px var(--accent-glow)',
          }}>
            {(u.nickname || u.full_name || '?').slice(0, 2).toUpperCase()}
          </div>
          <div className="col" style={{ alignItems: 'center', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="display" style={{ fontSize: 22 }}>{u.nickname || '—'}</span>
              {isCaptain && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="var(--gold)" title="Капитан">
                  <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm2 3h10v1H7v-1z" />
                </svg>
              )}
            </div>
            {u.full_name && (
              <span style={{ fontSize: 14, color: 'var(--fg-muted)' }}>{u.full_name}</span>
            )}
          </div>
        </div>

        {/* Stats grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 24 }}>
          <div style={{ padding: '14px 16px', background: 'var(--surface)', borderRadius: 'var(--r-md)', border: '1px solid var(--line)' }}>
            <div className="mono" style={{ fontSize: 9, color: 'var(--fg-faint)', letterSpacing: '0.14em', marginBottom: 6 }}>РОЛЬ</div>
            <RoleBadge role={member.role} />
            {isCaptain && (
              <div style={{ marginTop: 4 }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--gold)', letterSpacing: '0.1em' }}>КАПИТАН</span>
              </div>
            )}
          </div>
          {u.premier_rating > 0 && (
            <div style={{ padding: '14px 16px', background: 'var(--surface)', borderRadius: 'var(--r-md)', border: '1px solid var(--line)' }}>
              <div className="mono" style={{ fontSize: 9, color: 'var(--fg-faint)', letterSpacing: '0.14em', marginBottom: 6 }}>PREMIER</div>
              <PremierBadge rating={u.premier_rating} size="sm" />
            </div>
          )}
          {u.education_name && (
            <div style={{ padding: '14px 16px', background: 'var(--surface)', borderRadius: 'var(--r-md)', border: '1px solid var(--line)', gridColumn: u.premier_rating > 0 ? 'auto' : 'span 2' }}>
              <div className="mono" style={{ fontSize: 9, color: 'var(--fg-faint)', letterSpacing: '0.14em', marginBottom: 6 }}>УЧЁБА</div>
              <span style={{ fontSize: 12, color: 'var(--fg-soft)' }}>{u.education_name}</span>
            </div>
          )}
          {u.steam_profile && (
            <div style={{ padding: '14px 16px', background: 'var(--surface)', borderRadius: 'var(--r-md)', border: '1px solid var(--line)', gridColumn: 'span 2' }}>
              <div className="mono" style={{ fontSize: 9, color: 'var(--fg-faint)', letterSpacing: '0.14em', marginBottom: 6 }}>STEAM</div>
              <a href={u.steam_profile} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--accent-bright)', textDecoration: 'none', wordBreak: 'break-all' }}>
                {u.steam_profile.replace('https://steamcommunity.com/', '')}
              </a>
            </div>
          )}
        </div>

        {canJoin && user && (
          <Btn full onClick={onJoin} disabled={joining}>
            {joining ? t('common.sending') : t('register.sendRequest')}
          </Btn>
        )}
        {canJoin && !user && (
          <Btn full variant="ghost" onClick={() => useAppStore.getState().go('auth')}>
            {t('teams.ctaLogin')}
          </Btn>
        )}
      </div>
    </BottomSheet>
  );
}

/* ── Team detail sheet ────────────────────────────────────── */
function TeamSheet({ team, open, onClose, user, onJoin, joining, joined }) {
  const { t } = useTranslation();
  const [selMember, setSelMember] = useState(null);

  useEffect(() => { if (!open) setSelMember(null); }, [open]);

  if (!team) return null;

  const slots = 5 - (team.players_count || team.members?.length || 0);
  const isMember = user && team.members?.some(m => m.user_id === user.id);
  const isCaptain = user && team.captain_id === user.id;
  const canJoin = !isCaptain && !isMember && team.status === 'recruiting' && slots > 0 && !joined;

  const statusColor = team.status === 'recruiting' ? 'var(--ok)' : team.status === 'full' ? 'var(--fg-muted)' : 'var(--danger)';

  return (
    <>
      <BottomSheet open={open} onClose={onClose} zIndex={200}>
        <div style={{ padding: '16px 20px 40px' }}>
          {/* Team header */}
          <div className="row gap-3" style={{ alignItems: 'center', marginBottom: 20 }}>
            <div className="center display" style={{
              width: 60, height: 60, borderRadius: 'var(--r-md)', flexShrink: 0,
              background: 'linear-gradient(135deg, var(--accent-deep) 0%, var(--surface-3) 100%)',
              border: '1px solid var(--line-accent)', fontSize: 16, letterSpacing: '0.04em',
              boxShadow: '0 0 20px -6px var(--accent-glow)',
            }}>{team.tag}</div>
            <div className="col grow" style={{ minWidth: 0 }}>
              <div className="display" style={{ fontSize: 24, lineHeight: 1.05, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{team.name}</div>
              <div className="row gap-2" style={{ marginTop: 4, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{t('teams.cap')}</span>
                <span style={{ fontSize: 12, fontWeight: 600 }}>{team.captain?.nickname || '—'}</span>
              </div>
            </div>
            <StatusBadge status={team.status} t={t} />
          </div>

          {/* Stats row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 24 }}>
            <div style={{ padding: '12px 14px', background: 'var(--surface)', borderRadius: 'var(--r-md)', border: '1px solid var(--line)', textAlign: 'center' }}>
              <div className="mono" style={{ fontSize: 9, color: 'var(--fg-faint)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>{t('teams.players')}</div>
              <div className="display" style={{ fontSize: 22 }}>{team.players_count || team.members?.length || 0}<span style={{ fontSize: 14, color: 'var(--fg-muted)' }}>/5</span></div>
            </div>
            {team.avg_rating > 0 && (
              <div style={{ padding: '12px 14px', background: 'var(--surface)', borderRadius: 'var(--r-md)', border: '1px solid var(--line)', textAlign: 'center' }}>
                <div className="mono" style={{ fontSize: 9, color: 'var(--fg-faint)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>Premier</div>
                <div className="display" style={{ fontSize: 22, color: 'var(--gold)' }}>{team.avg_rating.toLocaleString()}</div>
              </div>
            )}
            {slots > 0 && (
              <div style={{ padding: '12px 14px', background: 'var(--surface)', borderRadius: 'var(--r-md)', border: `1px solid ${statusColor}44`, textAlign: 'center' }}>
                <div className="mono" style={{ fontSize: 9, color: 'var(--fg-faint)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>{t('teams.slots')}</div>
                <div className="display" style={{ fontSize: 22, color: 'var(--ok)' }}>+{slots}</div>
              </div>
            )}
          </div>

          {/* Roster */}
          {team.members && team.members.length > 0 && (
            <div>
              <div className="mono" style={{ fontSize: 10, letterSpacing: '0.16em', color: 'var(--fg-faint)', marginBottom: 10 }}>
                СОСТАВ · {team.members.length} ИГРОК{team.members.length === 1 ? '' : team.members.length < 5 ? 'А' : 'ОВ'}
              </div>
              <div className="col" style={{ gap: 8, marginBottom: 24 }}>
                {team.members.map(m => {
                  const isCap = team.captain_id === m.user_id;
                  const u = m.user || {};
                  return (
                    <button key={m.id} onClick={() => setSelMember(m)} style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '12px 14px',
                      background: 'var(--surface)', border: '1px solid var(--line)',
                      borderRadius: 'var(--r-md)', cursor: 'pointer', width: '100%',
                      textAlign: 'left', transition: 'border-color .15s, background .15s',
                    }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--line-strong)'; e.currentTarget.style.background = 'var(--surface-3)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--line)'; e.currentTarget.style.background = 'var(--surface)'; }}
                    >
                      <Avatar name={u.nickname || u.full_name} size={38} fontSize={13} />
                      <div className="col grow" style={{ minWidth: 0, gap: 2 }}>
                        <div className="row gap-2" style={{ alignItems: 'center' }}>
                          <span style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {u.nickname || '—'}
                          </span>
                          {isCap && (
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="var(--gold)">
                              <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm2 3h10v1H7v-1z" />
                            </svg>
                          )}
                        </div>
                        {u.full_name && (
                          <span style={{ fontSize: 11, color: 'var(--fg-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {u.full_name}
                          </span>
                        )}
                      </div>
                      <div className="row gap-2" style={{ alignItems: 'center', flexShrink: 0 }}>
                        {u.premier_rating > 0 && <PremierBadge rating={u.premier_rating} size="sm" />}
                        <RoleBadge role={m.role} />
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--fg-faint)" strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Actions */}
          {canJoin && user && (
            <Btn full onClick={() => onJoin(team)} disabled={joining === team.id} icon={joining === team.id ? <Spinner size={14} /> : null}>
              {joining === team.id ? t('common.sending') : t('register.sendRequest')}
            </Btn>
          )}
          {canJoin && !user && (
            <Btn full variant="ghost" onClick={() => useAppStore.getState().go('auth')}>
              {t('teams.ctaLogin')}
            </Btn>
          )}
          {isMember && <Chip color="var(--ok)" icon="✓">{t('teams.youInTeam')}</Chip>}
          {isCaptain && <Chip color="var(--gold)" icon="★">{t('teams.youCaptain')}</Chip>}
          {joined && !isMember && !isCaptain && (
            <div style={{ textAlign: 'center', color: 'var(--ok)', fontSize: 13, fontFamily: 'var(--font-mono)' }}>
              ✓ Запрос отправлен
            </div>
          )}
        </div>
      </BottomSheet>

      <PlayerSheet
        member={selMember}
        team={team}
        open={!!selMember}
        onClose={() => { setSelMember(null); onClose(); }}
        onBack={() => setSelMember(null)}
        onJoin={() => { onJoin(team); setSelMember(null); }}
        joining={joining === team.id}
        user={user}
      />
    </>
  );
}

/* ── Team card (compact, fully clickable) ─────────────────── */
function TeamCard({ team, onOpen, t }) {
  const slots = 5 - (team.players_count || team.members?.length || 0);
  const place = placementOf(team.name);
  const pm = place ? PLACEMENT_META[place] : null;
  const restShadow = pm ? `0 0 0 1px ${pm.color}22, 0 10px 34px -12px ${pm.glow}` : '';
  return (
    <button onClick={() => onOpen(team)} style={{
      display: 'block', width: '100%', textAlign: 'left',
      background: 'none', border: 'none', padding: 0, cursor: 'pointer',
    }}>
      <Panel pad={0} style={{
        overflow: 'hidden', transition: 'box-shadow .2s, transform .15s', position: 'relative',
        ...(pm ? {
          border: `1px solid ${pm.color}59`,
          background: place === 1 ? 'linear-gradient(160deg, rgba(230,183,60,0.10), var(--surface))' : 'linear-gradient(160deg, var(--surface-2), var(--surface))',
          boxShadow: restShadow,
        } : {}),
      }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = pm ? `0 0 0 1px ${pm.color}44, 0 16px 44px -10px ${pm.glow}` : '0 8px 30px -8px rgba(225,18,23,0.25)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = restShadow; }}
      >
        {/* champion corner glow */}
        {place === 1 && (
          <div style={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(230,183,60,0.22), transparent 65%)', pointerEvents: 'none' }} />
        )}
        <div style={{ height: 3, background: pm ? `linear-gradient(90deg, ${pm.deep}, ${pm.color})` : 'linear-gradient(90deg, var(--accent-deep), var(--accent-bright))' }} />
        <div style={{ padding: '18px 20px', position: 'relative' }}>
          {/* Header */}
          <div className="row gap-3" style={{ alignItems: 'center', marginBottom: 14 }}>
            <div className="center display" style={{
              width: 50, height: 50, borderRadius: 'var(--r-md)', flexShrink: 0,
              background: pm ? `linear-gradient(135deg, ${pm.deep} 0%, var(--surface-3) 100%)` : 'linear-gradient(135deg, var(--accent-deep) 0%, var(--surface-3) 100%)',
              border: `1px solid ${pm ? pm.color + '88' : 'var(--line-accent)'}`, fontSize: 14, letterSpacing: '0.04em',
              color: pm ? pm.color : undefined,
              boxShadow: pm ? `0 0 18px -6px ${pm.glow}` : '0 0 16px -6px var(--accent-glow)',
            }}>{team.tag}</div>
            <div className="col grow" style={{ minWidth: 0 }}>
              <div className="row gap-2" style={{ alignItems: 'center', minWidth: 0 }}>
                {place === 1 && <span style={{ color: pm.color, flexShrink: 0, display: 'flex' }}><TrophyIco size={15} color={pm.color} /></span>}
                <div className="display" style={{ fontSize: 18, lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: pm ? pm.color : undefined }}>{team.name}</div>
              </div>
              <div className="row gap-1" style={{ marginTop: 4, alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{t('teams.cap')}</span>
                <span style={{ fontSize: 11, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {team.captain?.nickname || '—'}
                </span>
              </div>
            </div>
            {pm ? <PlacementBadge rank={place} t={t} /> : <StatusBadge status={team.status} t={t} />}
          </div>

          {/* Stats */}
          <div className="row gap-4" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <div className="row gap-4">
              <div className="col" style={{ gap: 1 }}>
                <span className="mono" style={{ fontSize: 9, color: 'var(--fg-faint)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>{t('teams.players')}</span>
                <span className="display" style={{ fontSize: 18 }}>
                  {team.players_count || team.members?.length || 0}<span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>/5</span>
                </span>
              </div>
              {team.avg_rating > 0 && (
                <div className="col" style={{ gap: 1 }}>
                  <span className="mono" style={{ fontSize: 9, color: 'var(--fg-faint)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Avg</span>
                  <span className="display" style={{ fontSize: 18, color: 'var(--gold)' }}>{team.avg_rating.toLocaleString()}</span>
                </div>
              )}
              {slots > 0 && (
                <div className="col" style={{ gap: 1 }}>
                  <span className="mono" style={{ fontSize: 9, color: 'var(--fg-faint)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>{t('teams.slots')}</span>
                  <span className="display" style={{ fontSize: 18, color: 'var(--ok)' }}>+{slots}</span>
                </div>
              )}
            </div>
            <span style={{ color: 'var(--fg-faint)', fontSize: 13 }}>›</span>
          </div>

          {/* Member avatars strip */}
          {team.members && team.members.length > 0 && (
            <div className="row" style={{ marginTop: 12, gap: -6 }}>
              {team.members.slice(0, 5).map((m, i) => {
                const name = m.user?.nickname || m.user?.full_name || '?';
                return (
                  <div key={m.id} className="center" style={{
                    width: 26, height: 26, borderRadius: '50%', marginLeft: i > 0 ? -8 : 0,
                    background: pm ? `linear-gradient(135deg, ${pm.deep}, var(--surface-3))` : 'linear-gradient(135deg, var(--accent-deep), var(--surface-3))',
                    border: '2px solid var(--surface-2)',
                    fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 9,
                    color: pm ? pm.color : undefined,
                    zIndex: 5 - i, flexShrink: 0,
                  }}>
                    {name.slice(0, 1).toUpperCase()}
                  </div>
                );
              })}
              {team.members.length > 5 && (
                <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--fg-muted)', alignSelf: 'center' }}>+{team.members.length - 5}</span>
              )}
            </div>
          )}
        </div>
      </Panel>
    </button>
  );
}

function FaceitBadge({ lvl }) {
  const n = parseInt(lvl, 10) || 0;
  const color = n >= 10 ? '#ff5500' : n >= 8 ? '#ffcd00' : n >= 5 ? '#e17d00' : '#aaa';
  return (
    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: `${color}22`, color, border: `1px solid ${color}44` }}>LVL {lvl}</span>
  );
}

function ArchiveTeamCard({ team }) {
  const [expanded, setExpanded] = useState(false);
  const avgLvl = Math.round(team.players.reduce((s, p) => s + parseInt(p.faceitLvl, 10), 0) / team.players.length);
  const color = avgLvl >= 9 ? '#ff5500' : avgLvl >= 7 ? '#ffcd00' : avgLvl >= 5 ? '#e17d00' : 'var(--fg-muted)';

  return (
    <Panel pad={0} style={{ overflow: 'hidden' }}>
      <div style={{ height: 3, background: 'linear-gradient(90deg, var(--accent-deep), var(--accent-bright))' }} />
      <div style={{ padding: '20px 22px' }}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
          <div className="col" style={{ minWidth: 0 }}>
            <div className="display" style={{ fontSize: 18, lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{team.teamName}</div>
            <div className="row gap-2" style={{ marginTop: 5, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>кап:</span>
              <span style={{ fontSize: 12, fontWeight: 600 }}>{team.captainNick}</span>
            </div>
          </div>
          <div className="col" style={{ alignItems: 'flex-end', gap: 3, flexShrink: 0 }}>
            <span className="mono" style={{ fontSize: 9, color: 'var(--fg-faint)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Avg Faceit</span>
            <span className="display" style={{ fontSize: 22, color, lineHeight: 1 }}>{avgLvl}</span>
          </div>
        </div>
        <div className="col" style={{ gap: 5 }}>
          {(expanded ? team.players : team.players.slice(0, 3)).map((p, i) => (
            <div key={i} className="row gap-3" style={{ alignItems: 'center', padding: '7px 10px', background: 'var(--surface)', borderRadius: 'var(--r-sm)', border: '1px solid var(--line)' }}>
              <div className="grow mono" style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {p.nick}{p.nick === team.captainNick && <span style={{ color: 'var(--gold)', marginLeft: 6, fontSize: 11 }}>★</span>}
              </div>
              <FaceitBadge lvl={p.faceitLvl} />
              {p.faceit && <a href={p.faceit} target="_blank" rel="noreferrer" style={{ color: 'var(--fg-muted)', fontSize: 10, textDecoration: 'none', flexShrink: 0 }}>faceit ↗</a>}
            </div>
          ))}
          {team.players.length > 3 && (
            <button onClick={() => setExpanded(e => !e)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-bright)', fontSize: 12, fontFamily: 'var(--font-mono)', letterSpacing: '0.06em', padding: '4px 0', textAlign: 'left' }}>
              {expanded ? '↑ свернуть' : `↓ ещё ${team.players.length - 3} игрок(а)`}
            </button>
          )}
        </div>
      </div>
    </Panel>
  );
}

export default function TeamsPage() {
  const { user, fetchTeams, requestJoinTeam, go, fire } = useAppStore();
  const { t } = useTranslation();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('current');
  const [filter, setFilter] = useState('all');
  const [joining, setJoining] = useState(null);
  const [joined, setJoined] = useState({});
  const [selTeam, setSelTeam] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchTeams().then(data => {
      setTeams(data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [fetchTeams]);

  const filtered = (filter === 'recruiting'
    ? teams.filter(tm => tm.status === 'recruiting')
    : teams
  ).slice().sort((a, b) => (placementOf(a.name) || 99) - (placementOf(b.name) || 99));

  const handleJoin = useCallback(async (team) => {
    if (!user) { go('auth'); return; }
    setJoining(team.id);
    try {
      await requestJoinTeam(team.id);
      setJoined(j => ({ ...j, [team.id]: true }));
      fire(`Запрос в «${team.name}» отправлен капитану`, 'ok');
    } catch (e) {
      fire(e.message || 'Ошибка отправки запроса', 'error');
    } finally {
      setJoining(null);
    }
  }, [user, go, requestJoinTeam, fire]);

  const recruiting = teams.filter(tm => tm.status === 'recruiting').length;
  const totalPlayers = teams.reduce((s, tm) => s + (tm.players_count || tm.members?.length || 0), 0);

  return (
    <div className="page-in" style={{ position: 'relative', zIndex: 1, padding: '40px 0 80px' }}>
      <div className="container">

        <div style={{ marginBottom: 40 }}>
          <div className="eyebrow" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            CS2 OPEN · СКО — {t('teams.title')}
          </div>
          <h1 className="display" style={{ fontSize: 'clamp(30px,5vw,52px)', margin: '0 0 16px', lineHeight: 0.95 }}>
            {t('teams.title')}<br /><span className="grad-red">{t('teams.subtitle')}</span>
          </h1>
          <p style={{ margin: 0, fontSize: 15, color: 'var(--fg-soft)', maxWidth: 520, lineHeight: 1.55 }}>
            {t('teams.pageDesc')}
          </p>

          <div className="row gap-4 wrap" style={{ marginTop: 24 }}>
            {[
              { label: t('teams.title'), value: teams.length, color: 'var(--fg)' },
              { label: t('teams.players'), value: totalPlayers, color: 'var(--fg)' },
              { label: t('teams.filterRecruiting'), value: recruiting, color: 'var(--ok)' },
            ].map(s => (
              <div key={s.label} style={{ padding: '12px 20px', background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>{s.label}</span>
                <span className="display" style={{ fontSize: 26, color: s.color }}>{s.value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="row gap-2" style={{ marginBottom: 28, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="row gap-2" style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)', padding: 4 }}>
            {[['current', t('teams.seasonCurrent')], ['archive', t('teams.seasonArchive')]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} style={{
                padding: '8px 18px', borderRadius: 'var(--r-sm)', border: 'none', cursor: 'pointer',
                fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em',
                background: tab === k ? 'linear-gradient(120deg, var(--accent), var(--accent-deep))' : 'transparent',
                color: tab === k ? '#fff' : 'var(--fg-muted)', transition: 'all .2s',
              }}>{l}</button>
            ))}
          </div>
          {tab === 'current' && (
            <div className="row gap-2" style={{ alignItems: 'center' }}>
              <div className="row gap-1" style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)', padding: 4 }}>
                {[['all', t('teams.filterAll')], ['recruiting', t('teams.filterRecruiting')]].map(([k, l]) => (
                  <button key={k} onClick={() => setFilter(k)} style={{
                    padding: '6px 14px', borderRadius: 'var(--r-sm)', border: 'none', cursor: 'pointer',
                    fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em',
                    background: filter === k ? 'var(--surface-3)' : 'transparent',
                    color: filter === k ? 'var(--fg)' : 'var(--fg-muted)', transition: 'all .2s',
                  }}>{l}</button>
                ))}
              </div>
              {user && (
                <Btn size="sm" variant="ghost" onClick={() => go('profile')}>
                  {t('teams.myTeam')} →
                </Btn>
              )}
            </div>
          )}
        </div>

        {tab === 'archive' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))', gap: 20 }}>
            {ARCHIVE_TEAMS.map(team => <ArchiveTeamCard key={team.teamName} team={team} />)}
          </div>
        )}

        {tab === 'current' && (
          loading ? (
            <div className="center" style={{ padding: '80px 0' }}><Spinner size={36} /></div>
          ) : filtered.length === 0 ? (
            <div className="center col" style={{ gap: 16, padding: '80px 0', textAlign: 'center' }}>
              <div className="center" style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--surface-2)', border: '1px solid var(--line)' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--fg-muted)" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 6 }}>
                  {filter === 'recruiting' ? t('teams.noRecruiting') : t('teams.noTeams')}
                </div>
                <div style={{ fontSize: 13, color: 'var(--fg-muted)' }}>
                  {filter === 'recruiting' ? t('teams.tryShowAll') : t('teams.registerFirst')}
                </div>
              </div>
              {filter === 'recruiting' && (
                <Btn size="sm" variant="ghost" onClick={() => setFilter('all')}>{t('teams.showAll')}</Btn>
              )}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))', gap: 16 }}>
              {filtered.map(team => (
                <TeamCard
                  key={team.id}
                  team={joined[team.id] ? { ...team, status: 'closed' } : team}
                  onOpen={setSelTeam}
                  t={t}
                />
              ))}
            </div>
          )
        )}

        {tab === 'current' && !user && (
          <div style={{ marginTop: 48, padding: '32px 36px', background: 'linear-gradient(120deg, rgba(225,18,23,0.08), transparent)', border: '1px solid var(--line-accent)', borderRadius: 'var(--r-xl)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
            <div>
              <div className="display" style={{ fontSize: 22, marginBottom: 8 }}>{t('teams.ctaTitle')}</div>
              <div style={{ fontSize: 14, color: 'var(--fg-soft)' }}>{t('teams.ctaDesc')}</div>
            </div>
            <div className="row gap-3 wrap" style={{ maxWidth: '100%', minWidth: 0 }}>
              <Btn onClick={() => go('register')}>{t('teams.ctaRegister')}</Btn>
              <Btn variant="ghost" onClick={() => go('auth')}>{t('teams.ctaLogin')}</Btn>
            </div>
          </div>
        )}
      </div>

      <TeamSheet
        team={selTeam}
        open={!!selTeam}
        onClose={() => setSelTeam(null)}
        user={user}
        onJoin={handleJoin}
        joining={joining}
        joined={selTeam ? joined[selTeam.id] : false}
      />
    </div>
  );
}
